import {
  BIDDER_AUDIENCES,
  fetchHelpArticle,
  fetchHelpArticles,
  fetchHelpCategories,
  fetchHouseContact,
  fetchHouseIdentity,
  formatHouseAddress,
  houseContactIsEmpty,
  houseLabel,
  recordHelpArticleFeedback,
  recordHelpArticleView,
  type HelpArticleDetail,
  type HelpArticleSummary,
  type HelpCategory,
  type HouseContact,
} from '@auctisync/api-client';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { EmptyState } from '../../components/bidder-web';
import { useBidderHouses } from '../../hooks/useBidderHouses';
import { supabase } from '../../lib/supabase';

/**
 * Help, in the site rather than out of it.
 *
 * NEW PAGE (web design handoff, `web/web-help.jsx`). Help on the bidder site was
 * a link to help.auctisync.com — which on a Model B build sends a bidder to
 * ANOTHER COMPANY'S DOMAIN to find out how to bid at this one. The app grew its
 * in-app help centre on 2026-09-01; this is the website's turn.
 *
 * 🔴 SAME CORPUS, SAME SCOPING, SAME FEEDBACK TABLE as the app and the public
 * help site. `fetchHelpArticles` prefers a house's own article over AuctiSync's
 * on the same slug, so a house that rewrote "How maximum bids work" gets ITS
 * wording here. Three surfaces reading three copies of that rule is how one
 * starts showing a house's answer while another shows ours.
 *
 * IT ASKS FOR THE BIDDER AUDIENCES EXPLICITLY. RLS returns every published
 * platform article to anyone, including the ones written for auction-house
 * staff — readable on purpose, so a prospective house can browse them on the
 * public site. Listing "Managing failed payments" to a bidder would be this
 * page failing to choose.
 *
 * THE CONTACT CARD STAYS VISIBLE. A help centre whose real answer is often "ask
 * the house" should not bury the way to do it — and the details are the house's
 * own, from `get_house_contact`, never invented.
 */
export function PublicHelp() {
  const { slug } = useParams();
  const { selected, active, discoverHouse } = useBidderHouses();

  const [categories, setCategories] = useState<HelpCategory[]>([]);
  const [articles, setArticles] = useState<HelpArticleSummary[]>([]);
  const [article, setArticle] = useState<HelpArticleDetail | null>(null);
  const [html, setHtml] = useState('');
  const [contact, setContact] = useState<HouseContact | null>(null);
  const [voted, setVoted] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const tenantId = selected?.tenant_id ?? active?.tenant_id ?? null;
  const houseName = active === null ? 'the auction house' : houseLabel(active);

  // ── the corpus ────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const [cats, arts] = await Promise.all([
        fetchHelpCategories(supabase, tenantId),
        fetchHelpArticles(supabase, {
          tenantId,
          audiences: BIDDER_AUDIENCES,
          order: 'popular',
        }),
      ]);
      if (cancelled) return;
      // A failed CATEGORY read is survivable — the articles still list, just
      // ungrouped. A failed ARTICLE read is not: there is nothing to show.
      setCategories(cats.categories);
      setArticles(arts.articles);
      setError(arts.error?.message ?? null);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  /*
   * Help has no auction of its own, so it discovers the house the way the sales
   * list does — from the open sales, and only when they ALL belong to one. On
   * the shared domain a mixed list has no single owner, and branding a help
   * centre for whichever house sorted first would be a lie about the rest.
   */
  useEffect(() => {
    if (selected) return;
    let cancelled = false;
    void (async () => {
      const { data } = await supabase
        .from('auctions')
        .select('tenant_id')
        .eq('is_visible', true)
        .in('status', ['active', 'preview']);
      if (cancelled) return;
      const rows = (data ?? []) as { tenant_id: string }[];
      const first = rows[0]?.tenant_id;
      if (first === undefined || !rows.every((r) => r.tenant_id === first)) return;
      const house = await fetchHouseIdentity(supabase, first);
      if (!cancelled) discoverHouse(house);
    })();
    return () => {
      cancelled = true;
    };
  }, [selected, discoverHouse]);

  useEffect(() => {
    if (tenantId === null) {
      setContact(null);
      return;
    }
    let cancelled = false;
    void fetchHouseContact(supabase, tenantId).then((c) => {
      if (!cancelled) setContact(c);
    });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  // ── one article ───────────────────────────────────────────────────
  useEffect(() => {
    if (slug === undefined) {
      setArticle(null);
      setHtml('');
      setVoted(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      const { article: art } = await fetchHelpArticle(supabase, slug, tenantId);
      if (cancelled) return;
      setVoted(null);
      if (!art) {
        setArticle(null);
        setHtml('');
        return;
      }
      setArticle(art);
      // Sanitised before injection. Authors are trusted (platform staff, or a
      // house's own staff via RLS), but house-authored markdown on a public
      // page should still never carry script — defence in depth, and the same
      // pipeline the public help site uses so identical markdown renders
      // identically on both.
      const rendered = await marked.parse(art.body_markdown, { async: true });
      if (!cancelled) setHtml(DOMPurify.sanitize(rendered));
      // Bump the view count via the anon-safe RPC; failures are silent.
      void recordHelpArticleView(supabase, art.slug);
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, tenantId]);

  const byCategory = useMemo(() => {
    const out = new Map<string, HelpArticleSummary[]>();
    for (const a of articles) {
      const key = a.category_id ?? 'other';
      out.set(key, [...(out.get(key) ?? []), a]);
    }
    return out;
  }, [articles]);

  const shownCategories = categories.filter((c) => (byCategory.get(c.id) ?? []).length > 0);
  const address = contact === null ? null : formatHouseAddress(contact.address);
  const hasContact = contact !== null && !houseContactIsEmpty(contact);

  return (
    <div className="w-wrap">
      <div style={{ paddingTop: 40 }}>
        <div className="w-crumbs" style={{ paddingTop: 0 }}>
          <Link to="/help">Help</Link>
          {article !== null ? (
            <>
              <span aria-hidden>›</span>
              <span style={{ color: 'var(--text)' }}>{article.title}</span>
            </>
          ) : null}
        </div>
        <h1 className="w-h1" style={{ marginTop: 10 }}>
          {article?.title ?? 'Help'}
        </h1>
        {article === null ? (
          <p className="w-body muted" style={{ marginTop: 10, maxWidth: 560 }}>
            How bidding works here, what happens after you win, and how to collect. If the answer is
            about a particular lot, ask {houseName} on the lot page.
          </p>
        ) : null}
      </div>

      <div className="wh">
        <nav className="wh-nav" aria-label="Help topics">
          <Link to="/help" className="wh-l" aria-current={slug === undefined ? 'page' : undefined}>
            All topics
          </Link>
          {shownCategories.map((c) => (
            <a key={c.id} href={`#cat-${c.slug}`} className="wh-l">
              {c.title}
            </a>
          ))}
        </nav>

        <div className="wh-art">
          {loading ? (
            <p className="muted">Loading help…</p>
          ) : error !== null ? (
            <EmptyState icon="!" title="Help could not be loaded" body={error} />
          ) : slug !== undefined && article === null ? (
            <EmptyState
              title="That article is not here"
              body="It may have been renamed. The topics beside this list everything we have."
              action={
                <Link to="/help" className="w-btn w-btn-out">
                  All topics
                </Link>
              }
            />
          ) : article !== null ? (
            <>
              {/* Sanitised above. */}
              <div dangerouslySetInnerHTML={{ __html: html }} />
              <div
                style={{
                  marginTop: 32,
                  paddingTop: 20,
                  borderTop: '1px solid var(--border)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  flexWrap: 'wrap',
                }}
              >
                {voted === null ? (
                  <>
                    <span className="muted" style={{ fontSize: 14 }}>
                      Was this helpful?
                    </span>
                    {[true, false].map((helpful) => (
                      <button
                        key={String(helpful)}
                        type="button"
                        className="w-btn w-btn-out w-btn-sm"
                        onClick={() => {
                          // Shown as answered immediately: the reader has
                          // finished with the question, and a spinner on a
                          // thumbs-up is the site talking about itself.
                          setVoted(helpful);
                          void recordHelpArticleFeedback(supabase, article.slug, helpful, tenantId);
                        }}
                      >
                        {helpful ? 'Yes' : 'No'}
                      </button>
                    ))}
                  </>
                ) : (
                  <span className="muted" style={{ fontSize: 14 }}>
                    {voted
                      ? 'Thanks — noted.'
                      : `Thanks. If you still need an answer, ${houseName} can help.`}
                  </span>
                )}
              </div>
            </>
          ) : articles.length === 0 ? (
            <EmptyState
              title="Nothing here yet"
              body={`${houseName} has not published help articles for bidders yet. Questions about a lot can go to them on the lot page.`}
            />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 34 }}>
              {shownCategories.map((c) => (
                <section key={c.id} id={`cat-${c.slug}`}>
                  <h2 className="w-h2" style={{ fontSize: 20, marginBottom: 6 }}>
                    {c.title}
                  </h2>
                  {c.description !== null ? (
                    <p className="muted" style={{ fontSize: 14, marginBottom: 14 }}>
                      {c.description}
                    </p>
                  ) : null}
                  <div
                    style={{
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--r-card)',
                      overflow: 'hidden',
                    }}
                  >
                    {(byCategory.get(c.id) ?? []).map((a) => (
                      <Link key={a.id} to={`/help/${a.slug}`} className="wh-q">
                        <span>
                          <b style={{ display: 'block', fontWeight: 600 }}>{a.title}</b>
                          <span className="muted" style={{ fontSize: 13 }}>
                            {a.summary}
                          </span>
                        </span>
                        <span aria-hidden>›</span>
                      </Link>
                    ))}
                  </div>
                </section>
              ))}
              {(byCategory.get('other') ?? []).length > 0 ? (
                <section id="cat-other">
                  <h2 className="w-h2" style={{ fontSize: 20, marginBottom: 14 }}>
                    Everything else
                  </h2>
                  <div
                    style={{
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--r-card)',
                      overflow: 'hidden',
                    }}
                  >
                    {(byCategory.get('other') ?? []).map((a) => (
                      <Link key={a.id} to={`/help/${a.slug}`} className="wh-q">
                        <span>
                          <b style={{ display: 'block', fontWeight: 600 }}>{a.title}</b>
                          <span className="muted" style={{ fontSize: 13 }}>
                            {a.summary}
                          </span>
                        </span>
                        <span aria-hidden>›</span>
                      </Link>
                    ))}
                  </div>
                </section>
              ) : null}
            </div>
          )}
        </div>

        <aside className="wh-side">
          <div className="wl-panel">
            <div className="wl-ph">
              <span className="w-lbl">Ask the house</span>
            </div>
            <div style={{ padding: 14, display: 'grid', gap: 10 }}>
              {hasContact ? (
                <>
                  {contact.phone !== null ? (
                    <a href={`tel:${contact.phone}`} className="w-btn w-btn-out w-btn-sm">
                      {contact.phone}
                    </a>
                  ) : null}
                  {contact.email !== null ? (
                    <a href={`mailto:${contact.email}`} className="w-btn w-btn-out w-btn-sm">
                      {contact.email}
                    </a>
                  ) : null}
                  {address !== null ? (
                    <p className="muted" style={{ fontSize: 13, margin: 0, lineHeight: 1.55 }}>
                      {address}
                    </p>
                  ) : null}
                  {contact.officeHours !== null ? (
                    <p className="muted" style={{ fontSize: 13, margin: 0 }}>
                      {contact.officeHours}
                    </p>
                  ) : null}
                </>
              ) : (
                <p className="muted" style={{ fontSize: 13, margin: 0, lineHeight: 1.55 }}>
                  No contact details have been published yet. A question about a particular lot can
                  go to {houseName} on that lot&apos;s page, where the answer is shown to every
                  bidder.
                </p>
              )}
              <Link to="/browse" className="w-btn w-btn-out w-btn-sm">
                Ask on a lot
              </Link>
            </div>
          </div>

          <div className="wl-panel">
            <div className="wl-ph">
              <span className="w-lbl">Your account</span>
            </div>
            <div style={{ padding: 6 }}>
              <Link to="/my/invoices" className="wh-q">
                Your invoices<span aria-hidden>›</span>
              </Link>
              <Link to="/my/payment-methods" className="wh-q">
                Cards on file<span aria-hidden>›</span>
              </Link>
              <Link to="/my/dashboard" className="wh-q">
                Your bidding<span aria-hidden>›</span>
              </Link>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
